import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Panel,
  Button,
  Select,
  StatusBadge,
  EmptyState,
  Skeleton,
  Chip,
} from '@/components/ui';
import { useAdminDeals, type AdminDealRow } from '@/hooks/use-admin-deals';

/**
 * 6-value pipeline status options for the filter <Select>.
 * Must speak the same pipeline-status vocabulary as the server endpoint
 * (research/phase-3.md § 11 risk #3 — do NOT use the 2-value share-status enum).
 */
const PIPELINE_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'Analyzing', label: 'Analyzing' },
  { value: 'In Market', label: 'In Market' },
  { value: 'LOI Submitted', label: 'LOI Submitted' },
  { value: 'Under Contract', label: 'Under Contract' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Passed', label: 'Passed' },
];

/**
 * <AllDealsAdmin> — all-deals oversight table for the `/admin?tab=deals` surface.
 *
 * 4-state (loading / error / empty / success). Driven by real `useAdminDeals`
 * TanStack Query hook (NOT the UserManagement useState+useEffect anti-pattern).
 *
 * Row click navigates straight from the row data (ADR 0007 — admin reads are
 * NOT audited; `row.publicShareId` already distinguishes the target):
 *   - publicShareId == null  → /builder?conversation=<id>&adminView=1 (real
 *     Builder, read-only admin mode)
 *   - publicShareId != null  → /deal/:shareId?adminView=1 (public admin view)
 *
 * Filter: status-only in Phase 3. Owner filter deferred (server endpoint
 * already accepts ?owner= but the UI affordance lands in a follow-up when the
 * owner-list resolution is available). First-page only (server default limit=20
 * bounds the list per spec "never unbounded"). Pagination UI deferred.
 */
export function AllDealsAdmin() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading, isError, refetch } = useAdminDeals({
    status: statusFilter || undefined,
  });

  // ── Row-click navigation (ADR 0007 — no audit; navigate straight from row) ──
  // `row.publicShareId` already tells us the target, so no detail fetch is
  // needed. INTERNAL → real Builder in read-only admin mode; PUBLIC SHARE →
  // the public deal route, both carrying `adminView=1`.
  const handleRowClick = (row: AdminDealRow) => {
    if (row.publicShareId == null) {
      navigate({
        to: '/builder',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: (prev: any) => ({
          ...prev,
          conversation: row.id,
          adminView: 1,
        }),
      });
    } else {
      navigate({
        to: '/deal/$shareId',
        params: { shareId: row.publicShareId },
        // dealRoute has no validateSearch; deal.tsx reads adminView via
        // URLSearchParams (ADR 0007 / Session 5.1).
        search: { adminView: 1 },
      });
    }
  };

  // ── Filter bar ─────────────────────────────────────────────────────────────
  const filterBar = (
    <div className="flex items-center gap-3">
      <Select
        options={PIPELINE_STATUS_OPTIONS}
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="py-1.5 px-3 text-sm min-w-[160px]"
        aria-label="Filter by status"
      />
    </div>
  );

  // ── Loading state ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Panel title="All Deals" right={filterBar}>
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="line" className="w-full h-8" />
          ))}
        </div>
      </Panel>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <Panel title="All Deals" right={filterBar}>
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <span className="text-sm font-mono text-danger">
            Failed to load deals
          </span>
          <Button
            variant="secondary"
            onClick={() => refetch()}
            className="px-4 py-2 text-xs"
          >
            Retry
          </Button>
        </div>
      </Panel>
    );
  }

  const conversations = data?.conversations ?? [];

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (conversations.length === 0) {
    return (
      <Panel title="All Deals" right={filterBar}>
        <EmptyState
          title="No deals found"
          description={
            statusFilter
              ? `No deals with status "${statusFilter}". Try a different filter.`
              : 'No deals have been created on the platform yet.'
          }
        />
      </Panel>
    );
  }

  // ── Success state ──────────────────────────────────────────────────────────
  return (
    <Panel
      title="All Deals"
      badge={data?.total != null ? String(data.total) : undefined}
      right={filterBar}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-panel-border">
              <th className="text-left py-2 pr-4 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                Deal
              </th>
              <th className="text-left py-2 pr-4 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                Owner
              </th>
              <th className="text-left py-2 pr-4 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                Market
              </th>
              <th className="text-right py-2 pr-4 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                Units
              </th>
              <th className="text-right py-2 pr-4 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                Basis
              </th>
              <th className="text-right py-2 pr-4 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                IRR
              </th>
              <th className="text-left py-2 pr-4 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                Status
              </th>
              <th className="text-left py-2 text-xs font-mono font-semibold text-text-muted tracking-[0.8px] whitespace-nowrap">
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {conversations.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row)}
                onKeyDown={(e) => {
                  // Keyboard reachability (a11y floor): a <tr> isn't natively
                  // focusable; the row is the sole open-deal affordance. Enter/Space
                  // open the deal (same as click). Space gets preventDefault to
                  // stop the page scrolling.
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(row);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open ${
                  row.title ?? 'untitled deal'
                } — read-only admin view`}
                data-testid={`deal-row-${row.id}`}
                className={`
                  border-b border-panel-border last:border-0
                  cursor-pointer transition-colors duration-150
                  hover:bg-accent-faint
                  focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent
                `}
              >
                {/* Deal title */}
                <td className="py-2.5 pr-4 text-text-bright font-body text-sm max-w-[200px] truncate">
                  {row.title ?? '—'}
                </td>
                {/* Owner */}
                <td className="py-2.5 pr-4 text-text font-body text-sm whitespace-nowrap">
                  {row.ownerName}
                </td>
                {/* Market */}
                <td className="py-2.5 pr-4 text-text font-body text-sm whitespace-nowrap">
                  {row.market ?? '—'}
                </td>
                {/* Units — numeric, monospace */}
                <td className="py-2.5 pr-4 text-text font-mono tabular-nums text-sm text-right whitespace-nowrap">
                  {row.units ?? '—'}
                </td>
                {/* Basis — numeric, monospace */}
                <td className="py-2.5 pr-4 text-text font-mono tabular-nums text-sm text-right whitespace-nowrap">
                  {row.basis ?? '—'}
                </td>
                {/* IRR — numeric, monospace */}
                <td className="py-2.5 pr-4 text-text font-mono tabular-nums text-sm text-right whitespace-nowrap">
                  {row.irr ?? '—'}
                </td>
                {/* Status — 6-value pipeline status badge */}
                <td className="py-2.5 pr-4">
                  <StatusBadge status={row.pipelineStatus ?? 'Analyzing'} />
                </td>
                {/* Share indicator chip — polarity: null=INTERNAL, non-null=PUBLIC SHARE
                    Uses EXISTING Chip variants only (no 6th variant):
                    - PUBLIC SHARE → variant="market" (warn-amber = externally exposed)
                    - INTERNAL     → variant="system" (muted/neutral)
                    (research/phase-3.md § 11 risk #5 — static literal, no interpolation) */}
                <td className="py-2.5">
                  {row.publicShareId == null ? (
                    <Chip variant="system" label="INTERNAL" />
                  ) : (
                    <Chip variant="market" label="PUBLIC SHARE" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination summary — first-page only in Phase 3 (server default limit=20
          bounds the list; full prev/next pagination UI deferred as a follow-up).
          Shows total count as a hint that more records may exist. */}
      {data?.hasMore && (
        <div className="mt-3 pt-3 border-t border-panel-border">
          <span className="text-xs font-mono text-text-muted">
            Showing first {conversations.length} of {data.total} deals
          </span>
        </div>
      )}
    </Panel>
  );
}
